import React, { useContext, useMemo } from 'react';

import { updateGoalProgressAction, updateGoalProgressOnServerAction } from 'actions/task';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import GoalProgressSlider from 'screens/common/sidepanel/Goals/GoalProgressSlider';
import { selectGoalFromGlobalGoals } from 'selectors/globalGoalSelector';
import styled from 'styled-components';

import { MESH_LIST_TABS } from 'constants/ProjectConstants';
import { getPageName } from 'constants/helper';
import { AddPeerIcon } from 'constants/icons';
import { GOAL_EVENTS1, sendGoalEvent } from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';

import AddToAgendaComponent from '../AddToAgendaComponent';
import DetailIconComponent from '../DetailIconComponent';
import MeshListContext from '../MeshListContext';
import TaskOrGoalName from '../TaskOrGoalName';
import { ListIemUpdatingCSS, ListItemCSS } from '../styles';
import { GoalRelativeDueDate } from './GoalDueDate';
import GoalProgressText from './GoalProgressText';
import GoalStatus from './GoalStatus';
import GoalToIds from './GoalToIds';

export default function GoalListItem({ goal: localGoal, onClickHandler, style, showAddToAgenda = false }) {
  const location = useLocation();

  const goal = useSelector(selectGoalFromGlobalGoals(localGoal?.id)) ?? localGoal;
  const pageName = useMemo(() => getPageName(location), [location.pathname]);

  const { dispatch, employee } = useContext(MeshListContext);

  return (
    <Container isUpdating={goal.isUpdating} onClick={onClickHandler} style={style}>
      <FirstRow>
        <TaskOrGoalName text={goal.text} macros={goal.macros} isCompleted={goal.status === 'COMPLETED'} />
        {showAddToAgenda ? <StyledAddToAgendaComponent type={MESH_LIST_TABS.goal.taskType} goal={goal} /> : null}
        <StyledDetailIconComponent
          tooltipText="Open Details Panel"
          taskId={goal.id}
          taskKey={goal.key}
          dispatch={dispatch}
          employee={employee}
        />
      </FirstRow>

      <SecondRow>
        <GoalStatus
          pageName={pageName}
          dispatch={dispatch}
          goalId={goal.id}
          goalKey={goal.key}
          progressLevel={goal.goalProgressLevel}
          goalClosureStatus={goal.goalClosureStatus}
        />
        <GoalToIds
          pageName={pageName}
          dispatch={dispatch}
          goalId={goal.id}
          goalKey={goal.key}
          employees={goal.toIds}
          padding="0"
          maxNodes={3}
          iconSize="fixed28px"
          renderEmptyView={(props) => (
            <AddPeerIcon
              {...props}
              color="white"
              circular
              size="fixed24px"
              style={{ marginLeft: goal?.toIds?.length ? '-6px' : '0px' }}
            />
          )}
        />
      </SecondRow>
      <ThirdRow>
        <GoalRelativeDueDate
          dispatch={dispatch}
          goalId={goal.id}
          goalKey={goal.key}
          dueDate={goal.dueDate}
          detailedStatus={goal.detailedStatus}
        />
        <GoalProgressText
          defaultDisplayValue={0}
          startValue={goal.startingProgressValue}
          achievedValue={goal.actual}
          targetValue={goal.target}
          progressLevel={goal.goalProgressLevel}
        />
      </ThirdRow>
      <div onClick={(e) => e.stopPropagation()}>
        <StyledGoalProgressSlider
          onChange={(newValue) => {
            dispatch(
              updateGoalProgressAction({
                id: goal.id,
                key: goal.key,
                goalProgressValue: newValue,
              })
            );
          }}
          onChangeCommitted={(newValue) => {
            sendGoalEvent({
              page: { pageName, flow: 'Activity' },
              eventName: GOAL_EVENTS1.UpdatedProgressBar,
              field: 'goalProgressValue',
              referrer: [routesForTracking.home.pageName, routesForTracking.profile.pageName].includes(pageName)
                ? 'MeshList'
                : 'Card',
            });
            dispatch(
              updateGoalProgressOnServerAction({
                id: goal.id,
                key: goal.key,
                goalProgressValue: newValue,
              })
            );
          }}
          targetValue={goal.target}
          initialValue={goal.startingProgressValue}
          unit={goal.measure}
          progressLevel={goal.goalProgressLevel}
          achieved={goal.actual}
        />
      </div>
    </Container>
  );
}

const StyledDetailIconComponent = styled(DetailIconComponent)`
  visibility: hidden;
`;

const StyledAddToAgendaComponent = styled(AddToAgendaComponent)``;

const FirstRow = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
`;

const SecondRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 5px;
  margin-bottom: 10px;
  align-items: center;
`;
const ThirdRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;
const Container = styled.div`
  position: relative;
  ${ListItemCSS};
  ${ListIemUpdatingCSS};
  padding: 18px 24px 0;
  cursor: pointer;
  &:hover {
    ${StyledAddToAgendaComponent} {
      visibility: visible;
    }
    ${StyledDetailIconComponent} {
      visibility: visible;
      border-radius: 50%;
      background-color: white;
    }
    // z-index: 100;
    box-shadow:
      0 1px 2px 0 ${(props) => props.theme.palette.shadows.card},
      0 2px 6px 2px ${(props) => props.theme.palette.shadows.card};
    background-color: ${(props) => props.theme.palette.background.blue.light.v2};
  }
`;

const StyledGoalProgressSlider = styled(GoalProgressSlider)`
  width: 100%;
  .MuiSlider-thumb {
    visibility: hidden;
    height: 15px;
    width: 20px;
    margin-top: -5px;
    margin-left: -12px;
  }
  &:hover .MuiSlider-thumb {
    visibility: visible;
  }
  .MuiSlider-rail {
    height: 6px;
  }
  .MuiSlider-track {
    height: 6px;
  }
  .MuiSlider-markLabel {
    display: none;
  }
  margin-bottom: 0;
`;
